import React from 'react';
import useApiContext from 'hooks/useApiContext';
import useRepo from 'hooks/useRepo';
import useCrmEvent from 'hooks/useCrmEvent';
import { M } from 'TOMA';
import { CustomLink } from 'components/CustomLink';
import routes from 'router/routes';
import { useTranslation } from 'react-i18next';
import { iCrmDashboardStats } from 'services/Crm/Crm.type';
import s from './CrmDashboardStats.module.scss';

const STATUS_COLORS: Record<string, string> = {
  new: '#3b82f6',
  contacted: '#f59e0b',
  qualified: '#8b5cf6',
  proposal: '#3730a3',
  negotiation: '#f97316',
  won: '#22c55e',
  lost: '#ef4444',
};

function CrmDashboardStats(): JSX.Element {
  const { crmRepository } = useApiContext();
  const { selectedEventId, setSelectedEventId, events, eventsLoading } =
    useCrmEvent();
  const { t } = useTranslation();

  const {
    response: stats,
    loading,
    error,
  } = useRepo(async () => {
    if (selectedEventId) {
      return await crmRepository.getDashboardStats(selectedEventId);
    }
    return null as iCrmDashboardStats | null;
  }, [selectedEventId]);

  return (
    <div>
      <div className={s.pageHeader}>
        <h2 className={s.pageTitle}>{t('crm:tab_dashboard')}</h2>
        <select
          value={selectedEventId ?? ''}
          onChange={(e) => setSelectedEventId(Number(e.target.value))}
          className={s.eventSelect}
        >
          {(events ?? []).map((event: any) => (
            <option key={event.id} value={event.id}>
              {event.name}
            </option>
          ))}
        </select>
      </div>

      <M.Loading loading={loading || eventsLoading} error={error} fullWiewport>
        {stats != null && (
          <>
            <div className={s.statsGrid}>
              <div className={s.statCard}>
                <div className={s.statIcon}>&#128101;</div>
                <div className={s.statInfo}>
                  <span className={s.statLabel}>
                    {t('crm:stat_total_prospects')}
                  </span>
                  <span className={s.statValue}>{stats.summary.total}</span>
                </div>
              </div>
              <div className={s.statCard}>
                <div className={s.statIcon}>&#9989;</div>
                <div className={s.statInfo}>
                  <span className={s.statLabel}>
                    {t('crm:stat_active_prospects')}
                  </span>
                  <span className={s.statValue}>{stats.summary.active}</span>
                </div>
              </div>
              <div className={s.statCard}>
                <div className={s.statIcon}>&#128293;</div>
                <div className={s.statInfo}>
                  <span className={s.statLabel}>
                    {t('crm:stat_hot_prospects')}
                  </span>
                  <span className={s.statValue}>{stats.summary.hot}</span>
                </div>
              </div>
              <div className={s.statCard}>
                <div className={s.statIcon}>&#127919;</div>
                <div className={s.statInfo}>
                  <span className={s.statLabel}>
                    {t('crm:stat_converted_month')}
                  </span>
                  <span className={s.statValue}>
                    {stats.summary.converted_this_month}
                  </span>
                </div>
              </div>
            </div>

            <div className={s.twoColumns}>
              <div className={s.card}>
                <h3 className={s.cardTitle}>{t('crm:pipeline_breakdown')}</h3>
                <div className={s.pipelineList}>
                  {stats.pipeline.map((item) => (
                    <div key={item.status} className={s.pipelineRow}>
                      <span className={s.pipelineLabel}>
                        {t(`crm:status_${item.status}`)}
                      </span>
                      <div className={s.pipelineBarWrap}>
                        <div
                          className={s.pipelineBar}
                          style={{
                            width: `${String(item.percentage)}%`,
                            backgroundColor:
                              STATUS_COLORS[item.status] ?? '#94a3b8',
                          }}
                        />
                      </div>
                      <span className={s.pipelineCount}>{item.count}</span>
                    </div>
                  ))}
                </div>
                <CustomLink to={routes.crmPipeline.path} className={s.viewLink}>
                  {t('crm:view_pipeline')} &rarr;
                </CustomLink>
              </div>

              <div className={s.card}>
                <h3 className={s.cardTitle}>{t('crm:conversion_stats')}</h3>
                <div className={s.conversionMain}>
                  <span className={s.conversionLabel}>
                    {t('crm:conversion_rate')}
                  </span>
                  <span className={s.conversionRate}>
                    {stats.conversion.rate}%
                  </span>
                </div>
                <div className={s.conversionBarWrap}>
                  <div
                    className={s.conversionBar}
                    style={{ width: `${String(stats.conversion.rate)}%` }}
                  />
                </div>
                <div className={s.conversionGrid}>
                  <div className={s.conversionItem}>
                    <span className={s.conversionItemLabel}>
                      {t('crm:created_30d')}
                    </span>
                    <span className={s.conversionItemValue}>
                      {stats.conversion.created_30d}
                    </span>
                  </div>
                  <div className={s.conversionItem}>
                    <span className={s.conversionItemLabel}>
                      {t('crm:converted_30d')}
                    </span>
                    <span className={s.conversionItemValue}>
                      {stats.conversion.converted_30d}
                    </span>
                  </div>
                  <div className={s.conversionItem}>
                    <span className={s.conversionItemLabel}>
                      {t('crm:avg_delay')}
                    </span>
                    <span className={s.conversionItemValue}>
                      {stats.conversion.avg_delay_days} {t('crm:days')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={s.card}>
              <h3 className={s.cardTitle}>{t('crm:recent_activity')}</h3>
              {stats.recent_activity.length === 0 ? (
                <p className={s.emptyText}>{t('crm:no_interactions')}</p>
              ) : (
                <div className={s.activityList}>
                  {stats.recent_activity.map((item) => (
                    <div key={item.id} className={s.activityItem}>
                      <div className={s.activityDot} />
                      <div className={s.activityContent}>
                        <div className={s.activityHeader}>
                          <span className={s.activityType}>
                            {t(`crm:type_${item.type}`)}
                          </span>
                          <span className={s.activitySubject}>
                            {item.subject}
                          </span>
                          {item.prospect_file?.company?.name != null && (
                            <span className={s.activityCompany}>
                              {item.prospect_file.company.name}
                            </span>
                          )}
                        </div>
                        <span className={s.activityDate}>
                          {item.interaction_date}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </M.Loading>
    </div>
  );
}

export default CrmDashboardStats;
